import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Layout, LayoutScrollable} from '@di-nxt/components';
import {SceneHeader, Screen, Tab} from '../../components';
import {t} from '../../localization';
import {
  SectionHeader,
  DataDisplay,
  DataBorder,
  SectionBorder,
  MemberSidebar,
} from './Components';
import {
  HoursEditor,
  AvailabilityEditor,
  DeliveryMethodsEditor,
  ReceivesShiftsEditor,
  EmploymentStartDateEditor,
  ShiftHoursEditor,
} from './Editors';
import {
  MembersLoader,
  useFavouriteMembers,
  useTeamMembers,
} from '../../services';

type Props = {
  teamId: number;
  memberId: number;
  onSelectTab?: (tabIndex: number) => void;
};

export const Member: React.FC<Props> = ({teamId, memberId, onSelectTab}) => {
  const members = useTeamMembers(teamId);
  const member = useMemo(() => members.find(m => m.membershipId === memberId), [
    memberId,
    members,
  ]);
  const [disabled, setDisabled] = useState(false);

  // Favourite members
  const {addFavouriteMember} = useFavouriteMembers();
  useEffect(() => {
    addFavouriteMember(memberId);
    // Component did mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnEditModeChanged = useCallback((editing: boolean) => {
    setDisabled(editing);
  }, []);

  const handleCallNumber = useCallback(
    () => window.open(`tel:${member?.mobileNo}`, '_blank'),
    [member?.mobileNo],
  );

  const handleSendMail = useCallback(
    () => window.open(`mailto:-`, '_blank'),
    [],
  );

  return (
    <Screen>
      <MembersLoader teamId={teamId} />
      <Layout vertical flex>
        {/** Header */}
        <SceneHeader title={member?.display} />
        {/** Tab */}
        <Tab
          titles={[
            t('screens.member.title'),
            t('screens.experience.title'),
            t('screens.absence.title'),
          ]}
          selectedIndex={0}
          onSelectTab={onSelectTab}
        />
        {/** Content */}

        <Layout horizontal flex>
          {/* Sidebar */}
          <MemberSidebar member={member} />
          <LayoutScrollable>
            <Layout vertical flex>
              {/** Editors */}
              <SectionHeader title={t('screens.member.sections.general')} />
              {/** Name */}
              <DataDisplay
                title={t('screens.member.fields.name')}
                data={member?.fullname ?? '-'}
                disabled={disabled}
              />
              <DataBorder />
              {/** Address - TODO */}
              <DataDisplay
                title={t('screens.member.fields.address')}
                data={'-'}
                disabled={disabled}
              />
              <DataBorder />
              {/** Date of birth - TODO */}
              <DataDisplay
                title={t('screens.member.fields.dateofbirth')}
                data={'-'}
                disabled={disabled}
              />
              <SectionBorder />
              <SectionHeader title={t('screens.member.sections.contact')} />
              {/** Phone */}
              <DataDisplay
                title={t('screens.member.fields.phone')}
                data={member?.mobileNo ?? '-'}
                onPress={handleCallNumber}
                kind="Copy"
                disabled={disabled}
              />
              <DataBorder />
              {/** Email - TODO */}
              <DataDisplay
                title={t('screens.member.fields.email')}
                data={'-'}
                onPress={handleSendMail}
                kind="Copy"
                disabled={disabled}
              />
              <SectionBorder />
              {member && (
                <>
                  <SectionHeader
                    title={t('screens.member.sections.employment')}
                  />
                  <HoursEditor
                    onEditModeChanged={handleOnEditModeChanged}
                    disabled={disabled}
                    member={member}
                  />
                  <DataBorder />
                  <ShiftHoursEditor
                    field="maxDailyHours"
                    title={t('screens.member.fields.shifthours.max')}
                    onEditModeChanged={handleOnEditModeChanged}
                    disabled={disabled}
                    member={member}
                  />
                  <DataBorder />
                  <ShiftHoursEditor
                    field="minDailyHours"
                    title={t('screens.member.fields.shifthours.min')}
                    onEditModeChanged={handleOnEditModeChanged}
                    disabled={disabled}
                    member={member}
                  />
                  <DataBorder />
                  <AvailabilityEditor
                    member={member}
                    disabled={disabled}
                    onEditModeChanged={handleOnEditModeChanged}
                  />
                  <DataBorder />
                  <DeliveryMethodsEditor
                    member={member}
                    disabled={disabled}
                    onEditModeChanged={handleOnEditModeChanged}
                  />
                  <DataBorder />
                  <ReceivesShiftsEditor
                    member={member}
                    disabled={disabled}
                    onEditModeChanged={handleOnEditModeChanged}
                  />
                  <DataBorder />
                  <DataDisplay
                    title={t('screens.member.fields.personalcar')}
                    data={member?.hasCar}
                    disabled={disabled}
                  />
                  <DataBorder />
                  <EmploymentStartDateEditor
                    member={member}
                    disabled={disabled}
                    onEditModeChanged={handleOnEditModeChanged}
                  />
                  <DataBorder />
                </>
              )}
            </Layout>
          </LayoutScrollable>
        </Layout>
      </Layout>
    </Screen>
  );
};
